/**
 * ParityAudit (v6.36.27) — Super-admin panel that compares the digital
 * question bank to the print BLM manifest for the ACCESS-aligned (highest-
 * stakes) assessment per Cesar Directive non-negotiable #3.
 *
 * v6.36.27 — Adds the "Bake Missing PDFs" sprint button:
 *   - One click schedules a print-bake for every missing student/teacher
 *     PDF in the currently-viewed tier.
 *   - Idempotent (resume mode); existing PDFs in GridFS are skipped.
 *   - Live progress polled every 2s from /parity/bake-progress.
 *
 * Surfaces every cell (grade × modality) and flags any mismatch:
 *   - no_digital_questions
 *   - missing_student_pdf
 *   - missing_teacher_pdf
 */
import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  ArrowLeft, BookOpenCheck, RefreshCw, CheckCircle2,
  AlertOctagon, Loader2, Flame, Trash2, Clock, ShieldCheck,
} from 'lucide-react';
import { formatTimeAgo } from '../utils/timeAgo';
import ParityNumber, { ParityGovernanceBanner } from '../components/ParityNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIERS = [
  { value: 'A', label: 'Tier A (highest stakes)' },
  { value: 'B', label: 'Tier B' },
  { value: 'C', label: 'Tier C' },
];

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATUSES = new Set([
  'complete', 'complete_with_failures', 'failed', 'aborted', 'idle',
]);

const ParityAudit = () => {
  const [tier, setTier] = useState('A');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bakeStarting, setBakeStarting] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [progress, setProgress] = useState(null);
  const [autobakeInfo, setAutobakeInfo] = useState(null);
  const pollTimerRef = useRef(null);
  const completionToastedRef = useRef(false);

  const runAudit = async (t = tier) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/parity/audit`, { params: { tier: t } });
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/parity/bake-progress`);
      setProgress(res.data);
      if (res.data && TERMINAL_STATUSES.has(res.data.status)) {
        stopPolling();
        if (
          !completionToastedRef.current &&
          res.data.status !== 'idle' &&
          (res.data.ok || 0) + (res.data.fail || 0) + (res.data.skip || 0) > 0
        ) {
          completionToastedRef.current = true;
          if (res.data.status === 'complete') {
            toast.success(`Bake complete — ${res.data.ok} PDFs generated.`);
          } else if (res.data.status === 'complete_with_failures') {
            toast.warning(`Bake finished with ${res.data.fail} failures (${res.data.ok} OK).`);
          } else if (res.data.status === 'aborted') {
            toast.error(`Bake aborted: ${res.data.aborted_reason || 'unknown reason'}`);
          } else if (res.data.status === 'failed') {
            toast.error(`Bake failed: ${res.data.fatal_error || 'unknown error'}`);
          }
          runAudit(tier);
        }
      }
    } catch (e) {
      /* keep polling; transient network hiccups are fine */
    }
  };

  const startPolling = () => {
    if (pollTimerRef.current) return;
    completionToastedRef.current = false;
    fetchProgress();
    pollTimerRef.current = setInterval(fetchProgress, POLL_INTERVAL_MS);
  };

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const bakeMissing = async () => {
    if (!report) return;
    setBakeStarting(true);
    try {
      const res = await axios.post(`${API}/parity/bake-missing`, { tier });
      if ((res.data?.scheduled_count || 0) === 0) {
        toast.success('No missing PDFs — parity already met for this tier.');
        runAudit(tier);
      } else {
        toast.success(`Baking ${res.data.scheduled_count} missing PDFs in the background…`);
        startPolling();
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error === 'regen_already_running') {
        toast.warning('A press-PDF regeneration is already running. Watching that run instead.');
        startPolling();
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to start bake');
      }
    } finally {
      setBakeStarting(false);
    }
  };

  const cleanupOrphans = async () => {
    if (!report) return;
    setCleanupRunning(true);
    setShowCleanupConfirm(false);
    try {
      const res = await axios.post(
        `${API}/parity/cleanup-orphans`,
        { tier, confirm: true },
      );
      const removed = res.data?.deleted_count || 0;
      const notFound = res.data?.not_found_count || 0;
      const errs = res.data?.error_count || 0;
      if (removed === 0 && errs === 0) {
        toast.success('No orphan PDFs found — already clean.');
      } else if (errs > 0) {
        toast.warning(`Removed ${removed} orphan PDF(s) · ${errs} errors · see audit log.`);
      } else {
        toast.success(`Removed ${removed} orphan PDF(s)${notFound ? ` (${notFound} already gone)` : ''}.`);
      }
      runAudit(tier);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Cleanup failed — see backend logs.');
    } finally {
      setCleanupRunning(false);
    }
  };

  // Initial audit + check whether a bake is already in flight (page refresh case).
  useEffect(() => {
    runAudit('A');
    // Suppress completion toasts on initial mount — any terminal status
    // we see at load-time is a HISTORICAL run, not one this session
    // started. Only fresh bakes (triggered by clicking the button)
    // should toast on completion, and bakeMissing() clears this flag.
    completionToastedRef.current = true;
    fetchProgress().then(() => {
      // If a run is active on first load, resume live polling.
      setProgress((p) => {
        if (p && p.status === 'running') startPolling();
        return p;
      });
    });
    // Fetch the autobake last-run summary (read-only, no polling — the
    // operator can hit "Re-run" to refresh).
    axios
      .get(`${API}/parity/autobake-last`)
      .then((res) => setAutobakeInfo(res.data))
      .catch(() => setAutobakeInfo(null));
    return () => stopPolling();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const isRunning = progress && progress.status === 'running';
  // Split the mismatches by what's actually actionable from THIS button.
  //  - missingPdfCount:    bake will fix on click (student/teacher PDF absent).
  //  - needsQuestionsCount: digital questions missing for that grade × modality
  //    (must be authored, not baked — bake CAN'T resolve these). Almost always
  //    the orphan-file case from old grade-naming (e.g. 5_6 vs current 5-6).
  //  - orphanPdfCount:     ACTUAL PDF artifacts on those orphan cells — what
  //    the Cleanup orphans button will soft-delete.
  const missingPdfCount = report
    ? report.cells.reduce(
        (n, c) =>
          n +
          (c.issues.includes('missing_student_pdf') ? 1 : 0) +
          (c.issues.includes('missing_teacher_pdf') ? 1 : 0),
        0,
      )
    : 0;
  const needsQuestionsCount = report
    ? report.cells.filter((c) => c.issues.includes('no_digital_questions')).length
    : 0;
  const orphanPdfCount = report
    ? report.cells.reduce(
        (n, c) =>
          c.issues.includes('no_digital_questions')
            ? n + (c.student_pdf_count || 0) + (c.teacher_pdf_count || 0)
            : n,
        0,
      )
    : 0;

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="parity-audit-page">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/admin"><Button variant="ghost" size="sm" className="h-8 px-2"><ArrowLeft size={18} /></Button></Link>
        <BookOpenCheck className="w-7 h-7 text-[#002FA7]" />
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Print/Digital Parity Audit</h1>
      </div>
      <p className="text-sm text-gray-500 mb-6 ml-12">
        v6.36.30 — Cesar Directive non-negotiable #3. Verifies that the digital question bank and the print BLM manifest are in lockstep for every grade × modality. Bake missing PDFs or soft-delete orphan PDFs in a single click. Nightly auto-bake keeps parity green overnight; last-run status shown at bottom of page.
      </p>

      {/* Standing Parity Floor — owner directive 2026-06-11 */}
      <ParityGovernanceBanner />
      <div className="border-2 border-[#002FA7] bg-blue-50 p-4 mb-6" data-testid="parity-floor-banner">
        <div className="flex items-center gap-2 font-bold text-[#002FA7]" data-testid="parity-floor-title">
          <ShieldCheck className="w-5 h-5" />
          MOSAIC Parity Floor — standing release gate (owner directive 2026-06-11)
        </div>
        <div className="text-sm text-gray-700 mt-2 space-y-1.5" data-testid="parity-floor-body">
          <p>
            As of the v6.37.19 reconciliation (2026-06-13), the platform minimum floor is the empirically measured BVAF auditor scope:{' '}
            <span className="font-bold">
              BVAF scope <ParityNumber claim="40/40" kind="scope" />
              {' · '}digital <ParityNumber claim="39/40" kind="digital" />
              {' · '}print <ParityNumber claim="40/40" kind="print" />
              {' · '}full parity <ParityNumber claim="39/40" kind="full-parity" />
              {' · '}sources healthy <ParityNumber claim="40/40" kind="sources-healthy" />
              {' · 0 placeholder embeds · 0 parity regressions'}
            </span>, with the fail-loud print guard active. The floor ratchets upward as new verified visual-fix manifests are approved (including through the pictograph project). (1 known <span className="font-mono text-xs">digital_no_questions</span> exception — see <span className="font-mono text-xs">memory/PARITY_FLOOR_RECONCILIATION_v6_37_19.md</span>.)
          </p>
          <p>
            No release, content update, PDF regeneration, image/registry change, or renderer change may reduce MOSAIC below this floor. A passing deploy, endpoint, regenerated PDF, or smoke test is <span className="font-bold">not enough</span> — every meaningful change must preserve or improve the floor. Any audit below the floor <span className="font-bold text-red-700">blocks release</span> until the regression is classified and resolved.
          </p>
          <p className="text-xs text-gray-500">
            Required final dispositions: <span className="font-mono">PASSED_PARITY_FLOOR · FAILED_PARITY_FLOOR · INDETERMINATE_PENDING_EVIDENCE · BLOCKED_PENDING_OWNER_DECISION</span>. Full standard: <span className="font-mono">memory/MOSAIC_PARITY_STANDARD_SCOPE_LOCK.md</span>. Readiness language (pilot-ready, print-approved, etc.) remains withheld until B-DASH-3 + Gate-6 + final owner sign-off.
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <span className="text-sm text-gray-600">Form tier:</span>
        {TIERS.map((t) => (
          <Button
            key={t.value}
            variant={tier === t.value ? 'default' : 'outline'}
            size="sm"
            className={`h-8 ${tier === t.value ? 'bg-[#002FA7] text-white' : 'btn-sharp'}`}
            onClick={() => { setTier(t.value); runAudit(t.value); }}
            data-testid={`parity-tier-${t.value}`}
          >
            {t.label}
          </Button>
        ))}
        <Button onClick={() => runAudit()} disabled={loading} variant="outline" className="ml-auto btn-sharp h-8" data-testid="parity-refresh-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Auditing…</> : <><RefreshCw className="w-4 h-4 mr-2" /> Re-run</>}
        </Button>
        <Button
          onClick={bakeMissing}
          disabled={bakeStarting || isRunning || missingPdfCount === 0 || !report}
          className="bg-[#D03A1F] hover:bg-[#B22A0F] text-white btn-sharp h-8"
          data-testid="parity-bake-missing-btn"
        >
          {bakeStarting ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scheduling…</>
          ) : isRunning ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Baking…</>
          ) : (
            <><Flame className="w-4 h-4 mr-2" /> Bake {missingPdfCount} missing PDFs</>
          )}
        </Button>
        <Button
          onClick={() => setShowCleanupConfirm(true)}
          disabled={cleanupRunning || orphanPdfCount === 0 || !report}
          variant="outline"
          className="btn-sharp h-8 border-amber-500 text-amber-700 hover:bg-amber-50"
          data-testid="parity-cleanup-orphans-btn"
        >
          {cleanupRunning ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Cleaning…</>
          ) : (
            <><Trash2 className="w-4 h-4 mr-2" /> Cleanup {orphanPdfCount} orphan PDFs</>
          )}
        </Button>
      </div>

      {/* Cleanup orphans confirmation modal */}
      {showCleanupConfirm && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="parity-cleanup-confirm-modal"
          onClick={() => setShowCleanupConfirm(false)}
        >
          <div
            className="bg-white border-2 border-amber-500 max-w-lg w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 mb-4">
              <AlertOctagon className="w-7 h-7 text-amber-600 mt-0.5" />
              <div>
                <h2 className="font-bold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Soft-delete {orphanPdfCount} orphan PDF{orphanPdfCount === 1 ? '' : 's'}?
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Tier <span className="font-mono">{tier}</span> · {needsQuestionsCount} cell{needsQuestionsCount === 1 ? '' : 's'} flagged <span className="font-mono text-xs bg-amber-100 px-1">no_digital_questions</span>
                </p>
              </div>
            </div>
            <div className="bg-gray-50 border-l-4 border-amber-500 p-3 mb-4 text-sm space-y-2">
              <p>
                This removes only the <span className="font-mono">latest/</span> served revision for each orphan filename. In R2, every <span className="font-mono">revisions/&lt;ts&gt;/</span> key remains as immutable forensics evidence. In GridFS, older file rows for the same filename remain.
              </p>
              <p>
                Every delete is recorded to <span className="font-mono">audit_logs</span> with the operator email and bundle id, per the Cesar Directive destructive-op trail.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                className="btn-sharp h-9"
                onClick={() => setShowCleanupConfirm(false)}
                data-testid="parity-cleanup-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white btn-sharp h-9"
                onClick={cleanupOrphans}
                data-testid="parity-cleanup-confirm-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Confirm cleanup
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Progress banner */}
      {progress && (isRunning || (progress.done > 0 && !TERMINAL_STATUSES.has(progress.status))) && (
        <div className="border-2 border-amber-400 bg-amber-50 p-4 mb-6" data-testid="parity-bake-progress">
          <div className="flex items-center gap-3 mb-2">
            <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
            <div className="font-bold">Print-bake sprint in progress</div>
          </div>
          <div className="text-sm text-gray-700">
            <div data-testid="parity-bake-counts">
              <span className="font-mono">{progress.done || 0}</span> / <span className="font-mono">{progress.total || 0}</span>
              <span className="text-gray-500 mx-2">·</span>
              OK <span className="font-mono text-green-700">{progress.ok || 0}</span>
              <span className="text-gray-500 mx-2">·</span>
              Fail <span className="font-mono text-red-700">{progress.fail || 0}</span>
              <span className="text-gray-500 mx-2">·</span>
              Skipped <span className="font-mono text-gray-600">{progress.skip || 0}</span>
            </div>
            {progress.current && (
              <div className="text-xs text-gray-500 mt-1 truncate" data-testid="parity-bake-current">
                Now baking: <span className="font-mono">{progress.current}</span>
              </div>
            )}
            <div className="w-full bg-gray-200 h-2 mt-2 overflow-hidden">
              <div
                className="bg-amber-500 h-2 transition-all"
                style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%` }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Headline status */}
      {report && (
        <div
          className={`border-2 p-5 mb-6 ${report.in_full_parity ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}
          data-testid="parity-status-banner"
        >
          <div className="flex items-center gap-3">
            {report.in_full_parity ? (
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            ) : (
              <AlertOctagon className="w-8 h-8 text-red-600" />
            )}
            <div>
              <div className="font-bold text-lg" data-testid="parity-status-label">
                {report.in_full_parity
                  ? `Full Parity — ${report.total_cells} cells aligned`
                  : `${report.mismatched_cells} of ${report.total_cells} cells out of parity`}
              </div>
              {report.in_full_parity ? (
                <p className="text-sm text-gray-600 mt-1">
                  Every grade × modality has digital questions AND a matching student + teacher PDF.
                </p>
              ) : (
                <div className="text-sm text-gray-700 mt-2 space-y-1" data-testid="parity-status-breakdown">
                  <div className="flex items-center gap-2">
                    <Flame className="w-4 h-4 text-[#D03A1F]" />
                    <span>
                      <span className="font-bold font-mono">{missingPdfCount}</span> missing PDF{missingPdfCount === 1 ? '' : 's'} — fixable in one click via <span className="font-bold">Bake missing PDFs</span> above.
                    </span>
                  </div>
                  {needsQuestionsCount > 0 && (
                    <div className="flex items-start gap-2">
                      <AlertOctagon className="w-4 h-4 text-amber-600 mt-0.5" />
                      <span>
                        <span className="font-bold font-mono">{needsQuestionsCount}</span> cell{needsQuestionsCount === 1 ? '' : 's'} flagged <span className="font-mono text-xs bg-amber-100 px-1">no_digital_questions</span> — usually orphan PDFs from old grade-naming (e.g. <code>5_6</code> → now <code>5-6</code>).{' '}
                        {orphanPdfCount > 0 ? (
                          <>Click <span className="font-bold">Cleanup {orphanPdfCount} orphan PDFs</span> above to soft-delete them.</>
                        ) : (
                          <>Author the missing digital questions before next deploy.</>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Per-cell table */}
      {report && (
        <div className="border border-gray-300" data-testid="parity-cells-table">
          <div className="grid grid-cols-12 gap-2 p-3 bg-gray-100 text-xs font-bold uppercase tracking-wider text-gray-600">
            <div className="col-span-1">Status</div>
            <div className="col-span-2">Grade</div>
            <div className="col-span-2">Modality</div>
            <div className="col-span-2 text-right">Digital Qs</div>
            <div className="col-span-2 text-right">Student PDF</div>
            <div className="col-span-2 text-right">Teacher PDF</div>
            <div className="col-span-1">Issues</div>
          </div>
          {report.cells.map((c, i) => (
            <div
              key={i}
              className={`grid grid-cols-12 gap-2 p-3 text-sm border-t border-gray-200 ${c.in_parity ? '' : 'bg-red-50'}`}
              data-testid={`parity-cell-${c.grade_level}-${c.modality}`}
            >
              <div className="col-span-1">
                {c.in_parity
                  ? <CheckCircle2 className="w-4 h-4 text-green-600" aria-label="In parity" />
                  : <AlertOctagon className="w-4 h-4 text-red-600" aria-label="Out of parity" />}
              </div>
              <div className="col-span-2 font-mono">{c.grade_level || '(none)'}</div>
              <div className="col-span-2">{c.modality || '(none)'}</div>
              <div className="col-span-2 text-right font-mono">{c.digital_questions}</div>
              <div className="col-span-2 text-right font-mono">{c.student_pdf_count}</div>
              <div className="col-span-2 text-right font-mono">{c.teacher_pdf_count}</div>
              <div className="col-span-1 text-xs text-red-700">{c.issues.join(', ')}</div>
            </div>
          ))}
          {report.cells.length === 0 && (
            <div className="p-8 text-center text-gray-400 italic">No cells found for this exam type.</div>
          )}
        </div>
      )}

      {/* Nightly auto-bake observability footer (v6.36.30) */}
      {autobakeInfo && (
        <div
          className="mt-8 pt-4 border-t border-gray-200 text-xs text-gray-500 flex items-center gap-2 flex-wrap"
          data-testid="parity-autobake-footer"
        >
          <Clock className="w-3.5 h-3.5" />
          {autobakeInfo.status === 'never' ? (
            <span>
              <span className="font-bold">Nightly auto-bake:</span> not run yet on this pod. First cycle fires ~5 min after boot, then every 23h.
            </span>
          ) : (
            <span data-testid="parity-autobake-summary">
              <span className="font-bold">Last auto-bake event:</span>{' '}
              <span className="font-mono">{(autobakeInfo.latest_event?.action || '').replace('parity_autobake_', '')}</span>
              {' · '}
              <span>{formatTimeAgo(autobakeInfo.latest_event?.at)}</span>
              {autobakeInfo.last_complete && (
                <>
                  {' · '}
                  <span className="font-bold">Last completed run:</span>{' '}
                  <span className="font-mono text-green-700">{autobakeInfo.last_complete.ok_count ?? 0} baked</span>
                  {' · '}
                  <span className="font-mono text-red-700">{autobakeInfo.last_complete.fail_count ?? 0} failed</span>
                  {(autobakeInfo.last_complete.skip_count ?? 0) > 0 && (
                    <>
                      {' · '}
                      <span className="font-mono">{autobakeInfo.last_complete.skip_count} skipped</span>
                    </>
                  )}
                  {' · '}
                  <span>{formatTimeAgo(autobakeInfo.last_complete.at)}</span>
                </>
              )}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ParityAudit;
